import { useEffect, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useSession } from "@/hooks/use-session";

export function AuthGuard({ children }: { children: ReactNode }) {
  const [path, navigate] = useLocation();
  const { data, isLoading } = useSession();
  useEffect(() => {
    if (isLoading || data) return;
    // No user → punt to /login. Preserve the path the user was trying
    // to reach (incl. query string) in `next` so login can return them
    // there afterward. We don't gate on lock state any more — if data
    // is missing, the user is effectively logged out and the lock
    // overlay can't help them.
    const search = typeof window !== "undefined" ? window.location.search : "";
    const candidate = `${path}${search}`;
    // Only forward same-origin internal paths and never bounce back to
    // /login itself (would create a loop with `next=/login`).
    const safeNext =
      candidate.startsWith("/") &&
      !candidate.startsWith("//") &&
      !candidate.startsWith("/login")
        ? candidate
        : "/";
    const target = safeNext === "/" ? "/login" : `/login?next=${encodeURIComponent(safeNext)}`;
    navigate(target);
  }, [isLoading, data, path, navigate]);
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }
  if (!data) return null;
  return <>{children}</>;
}
